import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import { IEligibleRM } from "../../../../services/api.types";
import { getEligibleRMs, assignOwnerRM } from "../../../../services/carWash.service";

type CurrentRM = {
  _id: string;
  firstName?: string;
  lastName?: string;
  username?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  ownerId: string;
  onAssigned: () => void;
  onError: (message: string) => void;
  currentRM?: CurrentRM | null;
};

const rmLabel = (rm: IEligibleRM | CurrentRM): string =>
  `${rm.firstName ?? ""} ${rm.lastName ?? ""} (${rm.username})`.trim();

// Assign RM picker (260801-sa1). Fetches eligible relationship_manager-role
// admins on open and confirms the assignment via assignOwnerRM. Follows the
// Dialog/DialogTitle/DialogContent/DialogActions structure established by
// ChangeReviewDiffModal.tsx in this same directory.
const AssignRMModal = ({
  open,
  onClose,
  ownerId,
  onAssigned,
  onError,
  currentRM,
}: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [rms, setRms] = React.useState<IEligibleRM[]>([]);
  const [selected, setSelected] = React.useState<IEligibleRM | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setSelected(null);
    setLoading(true);
    getEligibleRMs()
      .then((list) => {
        setRms(list);
        // Pre-select the currently-assigned RM if it's present in the
        // fetched eligible list (e.g. not deactivated). If not found,
        // leave selected as null — the "Currently assigned" label alone
        // still communicates who it currently is.
        if (currentRM) {
          const match = list.find((rm) => rm._id === currentRM._id);
          if (match) setSelected(match);
        }
      })
      .catch((err: any) => onError(err?.message || "Failed to load relationship managers"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConfirm = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await assignOwnerRM(ownerId, selected._id);
      onAssigned();
      onClose();
    } catch (err: any) {
      onError(err?.message || "Failed to assign relationship manager");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("manageCarWashes.assignRMTitle")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {currentRM
            ? t("manageCarWashes.currentlyAssigned", {
                name: rmLabel(currentRM),
              })
            : t("manageCarWashes.noRMAssigned")}
        </Typography>
        {loading ? (
          <CircularProgress size={24} />
        ) : rms.length === 0 ? (
          <Typography color="text.secondary">
            {t("manageCarWashes.noRMsAvailable")}
          </Typography>
        ) : (
          <Autocomplete
            options={rms}
            getOptionLabel={rmLabel}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            value={selected}
            onChange={(_e, value) => setSelected(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("manageCarWashes.relationshipManager")}
                autoFocus
              />
            )}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!selected || submitting || loading || rms.length === 0}
          onClick={handleConfirm}
        >
          {t("common.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignRMModal;
